import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FCW } from '../../types.ts';
import merge from 'deepmerge';
import { isNil } from 'ramda';
import { getEventRequest } from '@/httpClient/requests/getEventRequest.ts';
import { isLocalDev } from '@/util/isLocalDev.ts';

import eventConfigMain from './configurations/base/events/config.yaml';
import translationConfig from './configurations/base/translation.yaml';
import layersConfig from './configurations/base/layers.yaml';
import eventConfigDev from './configurations/config-dev.yaml';
import configCi from './configurations/config-ci.yaml';

import { ConfigSlideId, EventConfiguration } from '@/eventConfig/types.ts';
import { EventConfig } from './EventConfigContext.tsx';
import { useAppConfig } from '@/appConfig/useAppConfig.ts';
import {
  loadLocaleEventConfigurations,
  loadSlideConfigurations,
  loadLocaleSlideConfigurations,
} from './EventConfigLoader.tsx';

const localeEventConfigurations = loadLocaleEventConfigurations();

const eventConfigurations = {
  main: eventConfigMain,
  dev: eventConfigDev,
  ...localeEventConfigurations,
};

const slideConfigurations = loadSlideConfigurations();
const localeSlideConfigurations = loadLocaleSlideConfigurations();

export interface EventConfigApi {
  eventConfig: EventConfiguration | null;
}

export const EventConfigProvider: FCW = ({ children }) => {
  const { api, eventCode, setIsTestEvent } = useAppConfig() || {};
  const [eventConfig, setEventConfig] = useState<EventConfiguration | null>(null);
  const eventCache = useRef<EventConfiguration | null>(null);

  const loadConfig = useCallback(async () => {
    let eventConfig;
    // check for inline event config
    const inlineConfig = loadInlineConfiguration();
    if (inlineConfig) {
      eventConfig = inlineConfig;
      // check for given eventCode in event cache
    } else if (isLocalDev()) {
      eventConfig = eventConfigurations.dev;
    } else {
      eventConfig = eventCache.current;
    }

    if (!eventConfig) {
      throw new Error('No event available from any source');
    }

    const cc = (eventConfig.meta.country || 'DE').toLowerCase() as keyof typeof eventConfigurations;
    const ccoverride = (configCi?.meta?.country || cc).toLowerCase() as keyof typeof localeSlideConfigurations;

    const localeConfig = eventConfigurations[ccoverride] || {};
    const defaultConfig = eventConfigurations.main;
    const defaultSlideConfigs = Object.values(slideConfigurations);
    const localeSlideConfigs = Object.values(localeSlideConfigurations[ccoverride]);

    const config: EventConfiguration = merge.all(
      [
        defaultConfig, // generic config from config.yaml
        layersConfig, // generic layers config
        ...defaultSlideConfigs, // generic slide configs
        localeConfig, // local config overrides
        ...localeSlideConfigs, // locale slide config overrides
        eventConfig, // actual event config
        translationConfig, // Common translation
        configCi || {}, // Anything in the CI config can override all prev values
      ],
      {
        arrayMerge: (_dst, src, _opt) => src, // always replace arrays
      }
    ) as EventConfiguration;

    // inject an id attribute for easier processing
    config.slides = Object.keys(config.slides).reduce((result, key) => {
      const slide = config.slides[key as ConfigSlideId];
      if (!slide.id) {
        const updatedNestedObj = { ...slide, id: key };
        return { ...result, [key]: updatedNestedObj };
      }
      return result;
    }, {}) as EventConfiguration['slides'];

    return config;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventCode]);

  useEffect(() => {
    if (!api || !setIsTestEvent) {
      return;
    }
    getEventRequest({ url: api.events })
      .then((res) => {
        const [eventConfig] = res;
        eventCache.current = eventConfig;
        if (eventConfig?.meta?.eventCode !== eventCode) {
          setTimeout(() => {
            window.location.href = window.location.origin;
          }, 10_000);

          throw Error(
            'Cookie event and API Event code mismatch. This is resolved in -remove tablet functionality-  ' +
              'so we can just sit on this error for now.'
          );
        }
      })
      .catch((e) => {
        console.error(e);
        console.log('Failure loading /events.');
      })
      .finally(() => {
        if (eventCode) {
          loadConfig()
            .then((config) => {
              setEventConfig(config);
              const isTestEvent = config.meta.test === true || config.meta.eventCode.indexOf('TEST') !== -1;
              setIsTestEvent(isTestEvent);
            })
            .catch(() => {
              console.log('getEventRequest failed. Redirecting to homepage.', eventCode, eventConfig);
              setTimeout(() => {
                window.location.href = window.location.origin;
              }, 3_000);
            });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventCode, api, loadConfig, setIsTestEvent]);

  const loadInlineConfiguration = () => {
    const inlineConfig = document.getElementById('ereg-event-config');

    if (inlineConfig && !isNil(inlineConfig.textContent)) {
      try {
        return JSON.parse(inlineConfig.textContent);
      } catch (_) {
        return false;
      }
    }
    return false;
  };

  return (
    <EventConfig.Provider
      value={{
        eventConfig,
      }}
    >
      {children}
    </EventConfig.Provider>
  );
};
